import React, { useContext, useState, useEffect } from "react";
import { Box, Container, Paper, Tab, Tabs, Typography } from "@mui/material";
import {
  AssessmentOutlined,
  PeopleOutlined,
  AccountBalanceOutlined,
  BalanceOutlined,
  AccountBalanceWalletOutlined,
  DirectionsCarOutlined,
} from "@mui/icons-material";
import IndividualLedger from "./IndividualLedger";
import SubaccountLedger from "./SubaccountLedger";
import PartyClosingBalances from "./PartyClosingBalances";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import TrialBalanceByGroup from "./TrialBalanceByGroup";
import CashBankBook from "./CashBankBook";
import TripReport from "./TripReport";
import { useNavigate, useLocation } from "react-router-dom";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import HouseIcon from "@mui/icons-material/House";
import { useLedgerState } from "../../Context/LedgerStateContext";
import { useAccountsNav } from "../../utils/moduleNav";
import PawnLedgerReports from "./pawnLedger/PawnLedgerReports";

/**
 * The ledger reports, in whichever shape the host module expects.
 *
 * Daily Tick and the Travel ERP get `LedgerTabs` below — the original six-tab
 * screen, unchanged.
 *
 * The pawn module gets `pawnLedger/PawnLedgerReports`: the same three reports
 * (Individual Ledger / Trial Balance / Cash-Bank Book) rebuilt in the shape of
 * the pawn Reports screen — chip switcher, one filter card with a View button,
 * and a results card that flexes into the leftover height. It is a separate
 * component tree rather than another `inPawn` branch in here because the two
 * layouts share no markup at all, and because the travel screens must not move.
 */
const Ledger = () => {
  const { inPawn } = useAccountsNav();
  return inPawn ? <PawnLedgerReports /> : <LedgerTabs />;
};

// Trip Report is travel-only; it stays last so nothing else shifts.
const TRIP_REPORT_TAB = 5;

const LedgerTabs = () => {
  const { defaultFY } = useContext(AutoTaskAuthContext) || {};
  const { ledgerState } = useLedgerState();
  const location = useLocation();
  const navigate = useNavigate();
  const { goHome } = useAccountsNav();

  // Check if we're coming back from accounts-entry
  const isReturningFromEntry = location.state?.fromAccountsEntry === true;

  // Use stored activeTab if returning from accounts-entry, otherwise default to 0
  const [activeTab, setActiveTab] = useState(
    isReturningFromEntry ? ledgerState.activeTab : 0,
  );

  useEffect(() => {
    // If returning from accounts-entry, restore the active tab
    if (isReturningFromEntry) {
      setActiveTab(ledgerState.activeTab);
    }
  }, [isReturningFromEntry, ledgerState.activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <Box
            onClick={() => navigate(-1)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              borderRadius: 2,
              bgcolor: "#037fd1",
              p: 0.5,
            }}
          >
            <ArrowBackRoundedIcon sx={{ fill: "#FFF" }} />
          </Box>
          <Box
            onClick={goHome}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              borderRadius: 2,
              bgcolor: "#037fd1",
              p: 0.5,
            }}
          >
            <HouseIcon sx={{ fill: "#FFF" }} />
          </Box>

          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Reports{" "}
            <span style={{ fontSize: "1rem" }}>
              {`(${defaultFY} - ${defaultFY + 1})`}
            </span>
          </Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="ledger tabs"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                minHeight: 64,
                fontSize: "1rem",
                fontWeight: 500,
              },
            }}
          >
            <Tab
              icon={<AssessmentOutlined />}
              iconPosition="start"
              label="Individual Ledger"
              id="ledger-tab-0"
              aria-controls="ledger-tabpanel-0"
            />
            <Tab
              icon={<PeopleOutlined />}
              iconPosition="start"
              label="Subaccount Ledger"
              id="ledger-tab-1"
              aria-controls="ledger-tabpanel-1"
            />
            <Tab
              icon={<AccountBalanceOutlined />}
              iconPosition="start"
              label="Party Closing Balances"
              id="ledger-tab-2"
              aria-controls="ledger-tabpanel-2"
            />
            <Tab
              icon={<BalanceOutlined />}
              iconPosition="start"
              label="Trial Balance"
              id="ledger-tab-3"
              aria-controls="ledger-tabpanel-3"
            />
            <Tab
              icon={<AccountBalanceWalletOutlined />}
              iconPosition="start"
              label="Cash/Bank Book"
              id="ledger-tab-4"
              aria-controls="ledger-tabpanel-4"
            />
            <Tab
              icon={<DirectionsCarOutlined />}
              iconPosition="start"
              label="Trip Report"
              id="ledger-tab-5"
              aria-controls="ledger-tabpanel-5"
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box role="tabpanel" hidden={activeTab !== 0}>
          {activeTab === 0 && <IndividualLedger tabIndex={0} />}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== 1}>
          {activeTab === 1 && <SubaccountLedger tabIndex={1} />}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== 2}>
          {activeTab === 2 && <PartyClosingBalances tabIndex={2} />}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== 3}>
          {activeTab === 3 && <TrialBalanceByGroup tabIndex={3} />}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== 4}>
          {activeTab === 4 && <CashBankBook tabIndex={4} />}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== TRIP_REPORT_TAB}>
          {activeTab === TRIP_REPORT_TAB && <TripReport />}
        </Box>
      </Paper>
    </Container>
  );
};

export default Ledger;
